import { SxProps, Theme } from '@mui/material'
import {
  BLOCKED_BADGE_BG,
  BLOCKED_BADGE_TEXT,
  SLATE_100,
  SLATE_200,
  SLATE_400,
  SLATE_500,
  SLATE_700,
  SLATE_950,
  UNKNOWN_CATEGORY_COLOR,
  WHITE,
  ZONE_BADGE_FALLBACK_BG,
  ZONE_BADGE_FALLBACK_TEXT,
} from '../config/constants'
import { getContrastText } from '../config/utils'

/**
 * Contenedor del resumen de celda. Lo aplica `CellSummaryPopper` al `Box` que
 * envuelve el contenido, no el contenido mismo, porque el `Popper` necesita un
 * nodo propio al que anclarse.
 */
export const TOOLTIP_SX: SxProps<Theme> = {
  bgcolor: WHITE,
  border: `1px solid ${SLATE_200}`,
  borderRadius: '12px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
  p: 0,
  maxWidth: 280,
}

/**
 * El resumen no intercepta el mouse: es informativo y taparía las celdas que
 * hay debajo. `zIndex` del tooltip para quedar sobre el Dialog de pasillo, que
 * también monta un Popper.
 */
export const POPPER_SX: SxProps<Theme> = {
  zIndex: (theme) => theme.zIndex.tooltip,
  pointerEvents: 'none',
}

const BADGE_BASE = {
  fontSize: '11px',
  fontWeight: 600,
  px: 0.75,
  py: 0.25,
  borderRadius: '4px',
  lineHeight: 1.4,
  whiteSpace: 'nowrap' as const,
}

export const cellTooltipStyles = {
  root: { p: 2, minWidth: 220, maxWidth: 260 } as SxProps<Theme>,

  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 1,
    mb: 1.5,
  } as SxProps<Theme>,

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    flexWrap: 'wrap',
  } as SxProps<Theme>,

  /** Sin color en la paleta cae al verde de respaldo, que siempre contrasta. */
  zoneBadge: (color?: string): SxProps<Theme> => ({
    ...BADGE_BASE,
    ...(color
      ? { bgcolor: color, color: getContrastText(color) }
      : { bgcolor: ZONE_BADGE_FALLBACK_BG, color: ZONE_BADGE_FALLBACK_TEXT }),
  }),

  blockedBadge: {
    ...BADGE_BASE,
    bgcolor: BLOCKED_BADGE_BG,
    color: BLOCKED_BADGE_TEXT,
    flexShrink: 0,
  } as SxProps<Theme>,

  position: {
    color: SLATE_500,
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1.3,
  } as SxProps<Theme>,

  unconfigured: { color: SLATE_400, fontSize: '13px' } as SxProps<Theme>,

  stockRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 0.5,
    mb: 0.25,
  } as SxProps<Theme>,

  stockValue: {
    color: SLATE_950,
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: 1,
  } as SxProps<Theme>,

  stockUnit: { color: SLATE_500, fontSize: '13px', fontWeight: 500 } as SxProps<Theme>,

  detailLine: { color: SLATE_400, fontSize: '12px' } as SxProps<Theme>,

  spacer: { mb: 1 } as SxProps<Theme>,

  separator: { borderTop: `1px solid ${SLATE_100}`, my: 1 } as SxProps<Theme>,

  brandList: { display: 'flex', flexDirection: 'column', gap: 0.5 } as SxProps<Theme>,

  brandRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  } as SxProps<Theme>,

  brandLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    overflow: 'hidden',
  } as SxProps<Theme>,

  brandDot: (color?: string): SxProps<Theme> => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    bgcolor: color ?? UNKNOWN_CATEGORY_COLOR,
    flexShrink: 0,
  }),

  brandName: {
    color: SLATE_700,
    fontSize: '13px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as SxProps<Theme>,

  brandQty: {
    color: SLATE_950,
    fontWeight: 600,
    fontSize: '13px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  } as SxProps<Theme>,

  hintSeparator: { borderTop: `1px solid ${SLATE_100}`, mt: 1, mb: 0.75 } as SxProps<Theme>,

  hint: {
    color: SLATE_400,
    fontSize: '11.5px',
    fontStyle: 'italic',
  } as SxProps<Theme>,
}
